/**
 * 3D COCKPIT's **structure shading**: what the aeroplane's own frame is shaded
 * like from the seat, and what the pilot's muzzle flashes do to it.
 *
 * The pure half — no `three`, no uniforms. `three/cockpit-interior.ts` is the
 * TSL that carries these numbers into `ramp-material.ts`'s walk, in the same
 * split `cockpit-dash-quad.ts`/`scene-cockpit-3d.ts` uses.
 *
 * ## The complaint, and what actually causes it — measured, not reasoned
 *
 * Phase 1's verdict after flying it: from the seat the hull is a flat dark wall
 * that *"reads as a hole rather than as canvas"*. The obvious mechanism, and
 * the one this file was first written around, is a **degeneracy in the sun**:
 * `SUN_DIRECTION_WORLD` is `(0, 0, -1)`, so a face's cosine is just its world
 * `nz`, and a fuselage interior's surfaces all have `nz ≈ 0` — one shade level,
 * one tone. That reasoning is clean, it predicts the symptom exactly, and on
 * this model it is **wrong**, which is the lessons file's oldest rule earning
 * its place again: a bug report names a percept, not a mechanism.
 *
 * What a WebGL capture of the real cockpit measured (`?renderer=webgl`, the
 * hatch `three/webgpu-support.ts` documents):
 *
 * 1. **There are no back faces in the frame at all.** Painting back faces red
 *    left the forward view unchanged, edge to edge. The enhanced Camel's
 *    cockpit is a *tub pressed into the decking* (`airframe.py`'s
 *    `cockpit_ranges`), so its walls face the pilot — everything he sees from
 *    the seat is front-facing, and a back-face rule would have shipped a
 *    treatment that changed nothing.
 * 2. **The flat wall is flat by the game's own table, not by geometry.** The
 *    `down` glance is 287,000 pixels of a single colour, `#594534` =
 *    `BARON.PAL` index 120, and that index's `SHADES.DAT` half-width is
 *    **0** — "draw it flat". `ramp-material.ts` therefore skips the walk
 *    entirely for those faces and returns the baked vertex colour, so **no
 *    cosine anyone computes can move them**. Index 120 is the aeroplane's
 *    *metal*: `airframe.py`'s `metal_of` gives it to the cabane struts, the
 *    flying wires, the undercarriage and the tail bracing — 500 of CAMEL LOD0's
 *    triangles, and the structure filling the pilot's downward view at 0.11 m
 *    from his eye.
 *
 * So the "wooden beams" the user asked to see lit are exactly the faces the
 * palette refuses to shade.
 *
 * ## The treatment: give the flat structure a ramp, and walk it by the real sun
 *
 * Two changes, both confined to the player's own material in cockpit-3D mode.
 *
 * 1. **A recovered ramp for a flat tone.** `world/landcover-shading.ts`
 *    (682df14) had this exact problem — a landcover tone with no shipped run to
 *    walk — and its answer is the method a 1990 artist with 256 colours used:
 *    take a luminance factor and land on the **nearest real palette entry at
 *    that hue**. `recoverRamp` is imported rather than reimplemented, so a
 *    strut lit by this is wearing `BARON.PAL` entries the game already ships
 *    and no free RGB enters the picture.
 * 2. **The mission's own sun.** The engine's constant is a horizontal light on
 *    the −Z horizon, chosen because it makes *banking* read
 *    (`ramp-shade.ts`). Inside a cockpit nothing banks relative to the pilot,
 *    so that constant is the one direction that cannot help; the recovered sun
 *    (`time-of-day.ts`'s `sunDirectionAt`) puts the light where the sky
 *    actually painted it, and the frame shades differently above and below.
 *
 * The walk itself is untouched, which is what keeps this a *treatment* rather
 * than a lighting model. There are still no lights in this renderer (AD-018 §8)
 * and nothing outside the player's own material in cockpit-3D mode can see any
 * of it.
 *
 * ## The numbers, and what they are
 *
 * INVENTED, all of them, and bounded rather than measured — the original draws
 * no 3D interior at all, so there is nothing to recover. What each is *for* is
 * written beside it, and `test/cockpit-interior.test.ts` pins the properties
 * that made them worth choosing rather than the values themselves.
 */

import type { Vec3 } from "@red-opus/engine";
import { SHADE_LEVEL_COUNT } from "@red-opus/formats";

/**
 * Shade levels per unit cosine — `shadeLevelFromCosine`'s own `floor(16·cos)`
 * quantiser, as the divisor that turns "so many rungs" into the cosine the walk
 * consumes. Every constant below is stated in **rungs** for that reason: a rung
 * is a real step of a real `BARON.PAL` ramp and a cosine is not.
 */
export const SHADE_LEVELS_PER_COSINE = SHADE_LEVEL_COUNT / 2;

/**
 * How much of the walk's range the structure's directional term is allowed to
 * span.
 *
 * A half, and the number's real job is the *bound* rather than the value: at
 * 0.5 the term reaches ±8 of the walk's 32 rungs, so a strut or a longeron can
 * never land on either end of its recovered ramp however the aeroplane is
 * flying. The ends are where a colour stops being timber and becomes a hole
 * again — which is the reading this whole treatment exists to answer.
 *
 * It is also a fidelity bound. A recovered ramp is not a shipped one: its outer
 * rungs are the palette's nearest entries at ±40 % luminance, and the further
 * a walk goes along it the more it is trusting a reconstruction. Half the range
 * keeps every drawn tone inside the part of the ramp the recovery is surest
 * about.
 */
export const INTERIOR_TRANSMITTANCE = 0.5;

/**
 * Rungs the structure sits above its anchor with the sun square on the beam,
 * i.e. with the directional term at zero.
 *
 * Two, upward. Skylight arrives through the whole envelope and through the
 * cockpit opening from every direction at once, so the neutral tone is a little
 * *lighter* than the aeroplane's own painted one rather than a little darker —
 * that is the difference between reading as lit timber and reading as a hole,
 * and it is the half of this treatment the user's word "shading" is about.
 */
export const INTERIOR_DAY_LEVELS = 2;

/**
 * Rungs the structure sits **below** its anchor at night, when there is no
 * directional term at all.
 *
 * Six, downward, and flat. `sunDirectionAt` returns `undefined` once the sun is
 * under the horizon and the honest answer to "which way is the light coming
 * from" is then *nowhere*: a night cockpit is lit by the exhaust and the
 * instruments, neither of which models a direction worth drawing. So the night
 * structure keeps the aeroplane's own recovered ramps and simply sits low on
 * them, which is dark without being black.
 *
 * It is deliberately *not* the day treatment with a dimmer sun. A direction the
 * scene does not have would be a lighting model invented for one view.
 */
export const INTERIOR_NIGHT_LEVELS = -6;

/**
 * How the structure's ramp walk is driven this frame: a sun vector already
 * scaled by `INTERIOR_TRANSMITTANCE`, and the neutral offset, both in the
 * cosine the walk consumes.
 *
 * Two numbers rather than four because the shader wants exactly this shape —
 * one `dot` and one `add`. Folding the span into the vector is what makes night
 * a *zero vector* rather than a special case: with no sun there is no
 * directional term, and `bias` alone is the whole answer.
 */
export interface InteriorLight {
  /** The sun direction times `INTERIOR_TRANSMITTANCE`, or all zeroes at night. */
  readonly sunScaled: Vec3;
  /** The neutral offset, in cosine — `INTERIOR_DAY_LEVELS` or `INTERIOR_NIGHT_LEVELS` over `SHADE_LEVELS_PER_COSINE`. */
  readonly bias: number;
}

/**
 * The sun elevation, as its **sine**, at which the cockpit is fully day-lit.
 * Below it the neutral rung slides down toward `INTERIOR_NIGHT_LEVELS`.
 *
 * **A taste call, and deliberately one constant so it is cheap to reverse: set
 * it to 0 and the behaviour is exactly what phase 2 shipped.** What it is for is
 * a cliff. Phase 2 had two answers and no third — `INTERIOR_DAY_LEVELS` while
 * the sun is up and `INTERIOR_NIGHT_LEVELS` the instant it is not, which is a
 * jump of **eight rungs** across the horizon. The game's own hour table
 * (`time-of-day.ts`'s `CELESTIAL_HOURS`) puts DAWN and DUSK at **7.5°** and
 * DAYTIME at **90°**, so under the phase-2 rule a cockpit at first light was lit
 * exactly like one at noon and then went black in one step. A cockpit is a hole
 * in the top of a fuselage: what reaches it is skylight through that hole, and
 * how much there is depends on how high the sun is, not merely on whether it has
 * cleared the horizon.
 *
 * 0.5 — 30° — is chosen for what it does to the three lights this game actually
 * briefs rather than for a photometric claim: DAWN and DUSK land a quarter of
 * the way up the ramp (about 3.9 rungs *below* the aeroplane's own tone, a
 * cockpit in deep shadow with a low sun raking across it), DAYTIME sits at the
 * top (2 rungs above), and night is unchanged. Three readings from three lights,
 * where there were two.
 */
export const INTERIOR_TWILIGHT_SINE = 0.5;

/**
 * The structure's lighting for a mission's sun, `undefined` meaning the sun is
 * down — which is what `time-of-day.ts`'s `sunDirectionAt` returns at night and
 * for a dogfight or duel that briefs no hour at all.
 *
 * Both of those take the night treatment, and that is the right answer for the
 * second as well as the first: with no briefed hour there is no sun to put
 * anywhere, and a flat interior is the reading that claims least.
 *
 * With a sun, the neutral rung is `INTERIOR_TWILIGHT_SINE`'s ramp between the
 * two — which is continuous at the horizon by construction, so the night branch
 * above is now the limit of the day one rather than a different answer.
 */
export function interiorLightFor(sun: Vec3 | undefined): InteriorLight {
  if (!sun) {
    return { sunScaled: { x: 0, y: 0, z: 0 }, bias: INTERIOR_NIGHT_LEVELS / SHADE_LEVELS_PER_COSINE };
  }
  const scale = INTERIOR_TRANSMITTANCE;
  return {
    sunScaled: { x: sun.x * scale, y: sun.y * scale, z: sun.z * scale },
    bias: interiorNeutralLevels(sun.y) / SHADE_LEVELS_PER_COSINE,
  };
}

/**
 * The neutral rung for a sun whose elevation has sine `elevationSine`, in shade
 * rungs — `INTERIOR_NIGHT_LEVELS` at the horizon, `INTERIOR_DAY_LEVELS` at and
 * above `INTERIOR_TWILIGHT_SINE`.
 *
 * Exported so the test can walk the game's own four hours through it rather than
 * restate the arithmetic.
 */
export function interiorNeutralLevels(elevationSine: number): number {
  if (!(INTERIOR_TWILIGHT_SINE > 0)) return INTERIOR_DAY_LEVELS;
  const day = Math.min(1, Math.max(0, elevationSine / INTERIOR_TWILIGHT_SINE));
  return INTERIOR_NIGHT_LEVELS + (INTERIOR_DAY_LEVELS - INTERIOR_NIGHT_LEVELS) * day;
}

/**
 * The cosine a structure face's ramp walk should use, given the dot of its
 * world normal with the (already-scaled) sun. The shader computes exactly this.
 */
export function interiorCosine(normalDotSun: number, light: InteriorLight): number {
  return normalDotSun + light.bias;
}

/**
 * Rungs a muzzle flash adds at zero distance and full strength.
 *
 * Six, upward — the same size of step the night bias takes downward, so the
 * flash is a *visible* event on the structure rather than a shimmer, and still
 * small enough that it is the aeroplane's own ramp lighting up and not a white
 * flash pasted over it. Brightening rather than tinting is not a compromise: a
 * walk up a `BARON.PAL` ramp is the only move that cannot leave the palette, and
 * the light rungs of this game's doped-linen and timber ramps are warmer than
 * their dark ones, so up the ramp *is* warmer.
 */
export const MUZZLE_FLASH_LEVELS = 6;

/**
 * Distance from the muzzle at which the flash is half as strong, in metres.
 *
 * 1.2 m, and it is a statement about the cockpit rather than about photometry.
 * The guns sit on the decking about 1.6 m ahead of the pilot's eye
 * (`gun-muzzles.ts` measures the Camel's breech at 1.585 m), and the structure
 * the user named — *"the plane chassis/wooden beams"*, which on this model is
 * the cabane struts, the bracing and the decking they stand on — is within a
 * metre or so of them, the nearest of it 0.11 m from his eye. At 1.2 m the near structure carries most of the
 * flash, the far end of the cockpit carries a fifth of it, and the tail carries
 * essentially none, which is the falloff a source in the nose actually has.
 */
export const MUZZLE_FLASH_HALF_DISTANCE_M = 1.2;

/**
 * The flash's spatial falloff: 1 at the muzzle, 1/2 at
 * `MUZZLE_FLASH_HALF_DISTANCE_M`, and inverse-square beyond it.
 *
 * `h² / (d² + h²)` rather than a clamped linear ramp, because a linear ramp has
 * a *radius* — a hard edge across the middle of a fuselage side, which at this
 * banding is a visible line — and this has none. It is the softened inverse
 * square a real point source has, with the singularity at `d = 0` removed by the
 * same `h²` that sets the half distance.
 */
export function muzzleFlashFalloff(distanceM: number): number {
  const half = MUZZLE_FLASH_HALF_DISTANCE_M * MUZZLE_FLASH_HALF_DISTANCE_M;
  return half / (distanceM * distanceM + half);
}

/** The rungs a flash of `strength` adds to a face `distanceM` from the muzzle — what the shader adds to the cosine, in rungs. */
export function muzzleFlashLevels(strength: number, distanceM: number): number {
  return MUZZLE_FLASH_LEVELS * strength * muzzleFlashFalloff(distanceM);
}

/**
 * How long one flash lives, as a multiple of the gun's own firing interval.
 *
 * Three. The lifetime has to be pinned to the gun's cadence rather than to a
 * wall-clock constant — otherwise the glow's beat and the gun's are unrelated
 * numbers — but *one* interval is the wrong multiple, and the reason is a
 * measurement rather than a taste: the port fires at
 * `gunRoundsPerSecond = 55` a gun, so an interval is **18.2 ms** and a 60 Hz
 * frame is 16.7. At one interval the term would sample somewhere between 1 and
 * 0 depending on where the frame landed inside the round, i.e. it would strobe
 * at the beat frequency between the display and the gun, which is a different
 * flicker on every machine. At three the term stays above 0.45 for the whole of
 * a held burst on any display and still falls to nothing 55 ms after the trigger
 * comes up, which is one frame's worth of tail.
 *
 * (`gunRoundsPerSecond` is CALIBRATION-PENDING rather than recovered —
 * `gunnery-tunables.ts` derives 55 from Mobley's "~300 rounds" and "5–6 s". The
 * multiple is what makes this robust to that number moving: at any rate, one
 * flash lasts three rounds.)
 */
export const MUZZLE_FLASH_INTERVALS = 3;

/** Seconds one flash lives for a gun firing at `roundsPerSecond`. */
export function muzzleFlashLifetimeS(roundsPerSecond: number): number {
  if (!(roundsPerSecond > 0)) return 0;
  return MUZZLE_FLASH_INTERVALS / roundsPerSecond;
}

/**
 * How long since a round last left the gun, and what the burst counter read when
 * that was decided.
 *
 * `burstRounds` is the engine's own "rounds fired in this uninterrupted burst"
 * and it is the only per-round signal the scene has: it steps by one for every
 * round `fireBurst` lets out and resets to 0 the instant the trigger is
 * released. Watching it *increase* is therefore watching the gun fire, at the
 * gun's own cadence, without the frame loop having to know anything about rate
 * of fire, jams, or an empty belt — a jammed or empty gun does not increment it.
 */
export interface MuzzleFlashState {
  sinceRoundS: number;
  lastBurstRounds: number;
}

/** Never fired: `Infinity` since the last round, so the strength is exactly 0 before a shot is taken. */
export function createMuzzleFlashState(): MuzzleFlashState {
  return { sinceRoundS: Number.POSITIVE_INFINITY, lastBurstRounds: 0 };
}

export interface MuzzleFlashUpdate {
  readonly dtS: number;
  /** `SimState.guns.burstRounds`. */
  readonly burstRounds: number;
}

/** Advances the timer by one frame. Mutates, because it is called once a frame for the life of a sortie. */
export function advanceMuzzleFlash(state: MuzzleFlashState, update: MuzzleFlashUpdate): void {
  if (update.burstRounds > state.lastBurstRounds) state.sinceRoundS = 0;
  else state.sinceRoundS += Math.max(update.dtS, 0);
  state.lastBurstRounds = update.burstRounds;
}

/** The flash's strength this frame, 0..1 — full at the round and linear to nothing over one lifetime. */
export function muzzleFlashStrength(state: MuzzleFlashState, roundsPerSecond: number): number {
  const lifetime = muzzleFlashLifetimeS(roundsPerSecond);
  if (lifetime <= 0) return 0;
  return Math.min(1, Math.max(0, 1 - state.sinceRoundS / lifetime));
}
